export type ResourceKind = "guide" | "qa";

export type Section = { heading: string; paragraphs: string[]; bullets?: string[] };

export type Resource = {
  slug: string;
  kind: ResourceKind;
  title: string;
  metaTitle: string;
  description: string;
  question?: string;
  shortAnswer?: string;
  readMinutes: number;
  updated: string;
  topic: string;
  sections: Section[];
};

export const resources: Resource[] = [
  {
    slug: "how-much-does-a-tutor-cost",
    kind: "qa",
    topic: "Costs",
    title: "How much does an English tutor cost?",
    metaTitle: "How Much Does an English Tutor Cost? | Smart Word Labs",
    description:
      "A plain-English breakdown of what parents pay for online English tutoring, what changes the price, and how small-group cohorts compare with 1-on-1 lessons.",
    question: "How much does an English tutor cost for a young child?",
    shortAnswer:
      "Online English tutoring for children is usually priced per lesson, and the two things that move the price most are group size and tutor qualifications. Small-group cohorts cost less per child than 1-on-1 lessons because the tutor's time is shared; 1-on-1 costs more but is fully tailored. At Smart Word Labs you can see exact current prices on the programmes section of the homepage, and the diagnostic call is free.",
    readMinutes: 3,
    updated: "2026-08-07",
    sections: [
      {
        heading: "What you are actually paying for",
        paragraphs: [
          "A lesson fee covers more than the time on screen. It pays for the tutor's planning, the materials used in the session, the feedback written afterwards, and the admin that keeps scheduling and progress records in one place.",
          "When you compare two quotes, compare what happens between lessons as well as the lesson itself. A cheaper lesson with no feedback loop often costs more in the long run because progress is harder to see.",
        ],
        bullets: [
          "Group size — the fewer children per tutor, the higher the price per hour",
          "Tutor qualifications and young-learner experience",
          "Whether homework review and written feedback are included",
          "Lesson length and how many sessions you book at once",
        ],
      },
      {
        heading: "Small group vs 1-on-1",
        paragraphs: [
          "1-on-1 is the right choice when a child needs a very specific fix — a speech-shy learner, an exam or interview deadline, or a gap that needs targeted work.",
          "Small groups are often better value for speaking confidence, because children hear peers of a similar level, take turns, and get used to speaking in front of others. Our cohorts are capped so every child still gets regular speaking turns.",
        ],
      },
      {
        heading: "How to keep costs sensible",
        paragraphs: [
          "Start with a free diagnostic call so you are not paying to find out the level. Then book a short block, look at the written feedback after each session, and only scale up once you can see movement.",
        ],
      },
    ],
  },
  {
    slug: "how-to-teach-phonics-at-home",
    kind: "guide",
    topic: "Phonics",
    title: "How to teach phonics at home without turning it into a battle",
    metaTitle: "How to Teach Phonics at Home (Ages 4-7) | Smart Word Labs",
    description:
      "A practical routine for teaching phonics at home in ten minutes a day: sound order, blending, decodable reading and what to do when your child gets stuck.",
    readMinutes: 6,
    updated: "2026-08-07",
    sections: [
      {
        heading: "Ten minutes beats an hour",
        paragraphs: [
          "Phonics works through frequent, short repetition. A focused ten minutes every day will do more for a four to seven year old than a long weekend session, because sound-letter links are built by retrieval, not by exposure.",
          "Pick a fixed slot — after breakfast, before a bath — so the routine does not have to be renegotiated each day.",
        ],
      },
      {
        heading: "Teach sounds, not letter names",
        paragraphs: [
          "Say the pure sound: /m/ rather than 'muh', /t/ rather than 'tuh'. Extra vowel sounds tacked onto consonants make blending much harder, because 'muh-a-tuh' does not sound like 'mat'.",
          "Introduce a handful of high-utility sounds first so your child can build real words almost immediately. A common order is s, a, t, p, i, n — those six letters already make sit, tap, pin, nap, pat and more.",
        ],
      },
      {
        heading: "A four-step daily routine",
        paragraphs: [
          "Keep the same shape every day so your child knows what is coming and can spend their attention on the reading itself.",
        ],
        bullets: [
          "Review: flash three or four known sounds, one second each",
          "Teach: introduce one new sound, say it, write it, find it in a word",
          "Blend: read three or four short words built only from known sounds",
          "Apply: read one or two sentences from a decodable book",
        ],
      },
      {
        heading: "When your child gets stuck",
        paragraphs: [
          "Do not spell the word out or ask them to guess from the picture. Point under each letter, ask for the sound, then sweep your finger left to right and ask them to push the sounds together.",
          "If a word fails twice, say it for them, have them repeat it, and move on. Returning to it tomorrow works better than grinding it out today.",
        ],
      },
      {
        heading: "Reading aloud still matters",
        paragraphs: [
          "Decodable practice builds the mechanics. Reading a favourite story aloud to your child builds the vocabulary and the appetite that make the mechanics worth having. Do both — they are not in competition.",
        ],
      },
      {
        heading: "When to bring in a tutor",
        paragraphs: [
          "If blending has not clicked after several weeks of consistent daily practice, or if your child is avoiding reading altogether, a specialist can usually find the specific blocker quickly. Our free diagnostic call is designed for exactly that conversation.",
        ],
      },
    ],
  },
  {
    slug: "phonics-vs-whole-word",
    kind: "qa",
    topic: "Phonics",
    title: "Phonics vs whole-word reading: what is the difference?",
    metaTitle: "Phonics vs Whole Word Reading Explained | Smart Word Labs",
    description:
      "The difference between phonics and whole-word (look-and-say) reading, why most schools teach phonics first, and how sight words still fit in.",
    question: "What is the difference between phonics and whole-word reading?",
    shortAnswer:
      "Phonics teaches children the sounds letters make so they can decode any new word. Whole-word (look-and-say) teaches children to recognise words as complete shapes. Phonics is the approach most English-speaking school systems now teach first, because it gives a child a strategy for words they have never seen before — but a small set of memorised high-frequency words is still useful alongside it.",
    readMinutes: 3,
    updated: "2026-08-07",
    sections: [
      {
        heading: "Why decoding scales and memorising does not",
        paragraphs: [
          "A child who can blend sounds can attempt any regular word, including ones nobody has taught them. A child relying on word shapes can only read the words they have already memorised, so the method runs out as texts get longer.",
          "That is the core practical difference, and it is why a decoding strategy is taught first.",
        ],
      },
      {
        heading: "Where sight words still help",
        paragraphs: [
          "English has common words that do not follow the usual patterns — said, one, was, the. Children learn these as wholes so that early sentences read smoothly. That is a small supporting list, not a replacement for decoding.",
        ],
      },
      {
        heading: "Signs your child is guessing rather than decoding",
        paragraphs: [
          "Watch for a child who reads 'horse' as 'pony', glances at the picture before the word, or reads the first letter and invents the rest. These are all signs to slow down and go back to sound-by-sound blending.",
        ],
      },
    ],
  },
  {
    slug: "online-tutoring-for-5-year-olds",
    kind: "qa",
    topic: "Getting started",
    title: "Does online tutoring actually work for 5 year olds?",
    metaTitle: "Does Online Tutoring Work for 5 Year Olds? | Smart Word Labs",
    description:
      "What online lessons look like for very young children, how long they should be, and the conditions that make them work at age four to six.",
    question: "Does online tutoring work for a 5 year old?",
    shortAnswer:
      "Yes, when the lesson is built for that age: short, highly interactive, with the child speaking most of the time rather than watching. What fails at this age is a long, lecture-shaped session. What works is roughly 25-30 minutes of games, turn-taking and repetition, with an adult nearby for the first few sessions.",
    readMinutes: 3,
    updated: "2026-08-07",
    sections: [
      {
        heading: "Set the room up once",
        paragraphs: [
          "Same table, same chair, headphones, screen at eye level, nothing else on the desk. Five year olds spend a surprising amount of energy on logistics, and removing that friction buys back attention for the lesson.",
        ],
      },
      {
        heading: "Stay nearby, but out of shot",
        paragraphs: [
          "For the first sessions sit within earshot to help with a frozen screen or a lost pencil, then step back. Children speak more freely once they realise the lesson is theirs.",
        ],
      },
      {
        heading: "Judge it on talking time",
        paragraphs: [
          "The single most useful thing to watch in a free assessment is how much of it your child spends actually speaking. Our lessons are designed so children speak for the majority of the session, and our cohorts are deliberately tiny so that stays true.",
        ],
      },
    ],
  },
  {
    slug: "is-my-child-behind-in-reading",
    kind: "guide",
    topic: "Progress",
    title: "Is my child behind in reading? How to tell, calmly",
    metaTitle: "Is My Child Behind in Reading? A Parent's Guide | Smart Word Labs",
    description:
      "How to judge whether your child's reading is genuinely behind, which signals matter at each age, and the practical next steps before you panic.",
    readMinutes: 5,
    updated: "2026-08-07",
    sections: [
      {
        heading: "Compare to a trajectory, not to a classmate",
        paragraphs: [
          "Reading development is uneven. A child can look behind in October and be comfortably fine by March, and children in the same class can be a year apart in reading with neither being a problem.",
          "The useful question is not 'is my child ahead of their friend?' but 'is my child moving forward month on month?' A child who is moving, even slowly, is usually fine. A child who is completely static for a term is worth investigating.",
        ],
      },
      {
        heading: "What to watch at each stage",
        paragraphs: [
          "These are conversation-starters with a teacher or tutor, not a diagnosis.",
        ],
        bullets: [
          "Ages 4-5: cannot hear the first sound in a word, or cannot blend two or three sounds into a word after months of practice",
          "Ages 6-7: still reading letter by letter without ever blending smoothly, or guessing words from the picture",
          "Ages 8-10: reads accurately but so slowly that meaning is lost by the end of the sentence",
          "Any age: actively avoids reading, or becomes distressed when asked",
        ],
      },
      {
        heading: "Separate decoding from comprehension",
        paragraphs: [
          "Ask your child to read a short passage aloud, then ask them to tell you what happened. A child who reads it fluently but cannot retell it has a comprehension or vocabulary gap. A child who struggles through the words but retells it well has a decoding gap. The two need different work.",
        ],
      },
      {
        heading: "Sensible next steps",
        paragraphs: [
          "Talk to the class teacher first — they see your child against thirty peers every day. Start ten minutes of daily reading at home. If nothing shifts over several weeks, get a structured assessment rather than adding more of the same practice.",
        ],
      },
      {
        heading: "Where we fit",
        paragraphs: [
          "Our free diagnostic call exists to place a child properly before any money changes hands, and to tell you honestly if what your child needs is not what we offer.",
        ],
      },
    ],
  },
  {
    slug: "preparing-for-a-private-school-interview",
    kind: "guide",
    topic: "Interview prep",
    title: "Preparing a young child for a private school interview",
    metaTitle: "Private School Interview Prep for Young Children | Smart Word Labs",
    description:
      "How to prepare a child aged 4-12 for a school interview or assessment day: what is actually assessed, how to rehearse without coaching, and what to do on the day.",
    readMinutes: 6,
    updated: "2026-08-07",
    sections: [
      {
        heading: "What schools are looking for",
        paragraphs: [
          "For younger children the interview is usually a friendly conversation and some play-based tasks. Assessors are watching whether a child can listen, take a turn, answer in more than one word, and stay engaged with an unfamiliar adult.",
          "For older children there is usually more reading aloud, some discussion of a book or an interest, and a few open questions with no right answer.",
        ],
      },
      {
        heading: "Rehearse the shape, not the script",
        paragraphs: [
          "Over-coached answers are obvious and count against a child. What genuinely helps is practising the shape of a good answer: say something, then add a reason or an example.",
          "Try a two-minute conversation at dinner where every answer has to have a 'because' in it. It is a small habit that transfers directly.",
        ],
      },
      {
        heading: "Questions worth practising",
        paragraphs: [
          "Use these as conversation prompts over a few weeks rather than a drill the night before.",
        ],
        bullets: [
          "Tell me about a book you have read recently. What happened in it?",
          "What do you like doing when you are not at school, and why?",
          "Tell me about something you found hard and kept trying at.",
          "If you could change one thing about your school day, what would it be?",
        ],
      },
      {
        heading: "Reading aloud",
        paragraphs: [
          "Many assessments include reading a short unseen passage. Practise reading aloud with expression, pausing at full stops, and — crucially — carrying on calmly after a mistake. Recovering well from a stumble reads as confidence.",
        ],
      },
      {
        heading: "The day itself",
        paragraphs: [
          "Sleep, breakfast, arrive early, and keep the car conversation light. Tell your child the school is trying to get to know them, not test them. Children who feel examined shrink; children who feel met open up.",
        ],
      },
      {
        heading: "Structured help",
        paragraphs: [
          "Our interview preparation programme runs mock conversations with feedback so a child has practised the experience, not memorised the answers. Details are on the programmes section of the homepage.",
        ],
      },
    ],
  },
  {
    slug: "small-group-vs-one-to-one-lessons",
    kind: "qa",
    topic: "Getting started",
    title: "Small group or 1-on-1 lessons: which should I choose?",
    metaTitle: "Small Group vs 1-on-1 English Lessons | Smart Word Labs",
    description:
      "When a small-group cohort beats private tuition for a young English learner, when 1-on-1 is worth the extra cost, and how to decide.",
    question: "Should my child do small-group or 1-on-1 English lessons?",
    shortAnswer:
      "Choose a small group when the goal is speaking confidence, fluency and motivation — peers create real reasons to talk. Choose 1-on-1 when there is a specific gap, a deadline such as an interview, or when your child is too anxious to speak in front of others yet. Many families start 1-on-1 and move into a cohort once confidence is there.",
    readMinutes: 3,
    updated: "2026-08-07",
    sections: [
      {
        heading: "What groups give you that private lessons cannot",
        paragraphs: [
          "Children learn a great deal from hearing a peer attempt the same sentence, get corrected, and try again. It normalises being wrong, which is the main thing standing between a shy child and fluency.",
          "Groups also create genuine communication: asking a partner a question is a real exchange in a way that answering a tutor is not.",
        ],
      },
      {
        heading: "What 1-on-1 gives you",
        paragraphs: [
          "Complete tailoring and maximum correction density. If your child has a particular pronunciation issue, a specific literacy gap, or an assessment in six weeks, private lessons will get there faster.",
        ],
      },
      {
        heading: "Group size is the number that matters",
        paragraphs: [
          "A 'small group' of twelve is a class. Ours are capped at a handful of children so every learner still speaks repeatedly in every session — that cap is what makes the group option work rather than just cheaper.",
        ],
      },
    ],
  },
];

export const guides = resources.filter((r) => r.kind === "guide");
export const questions = resources.filter((r) => r.kind === "qa");

export function getResource(slug: string) {
  return resources.find((r) => r.slug === slug);
}
